import { useEffect } from "react";
import { Link } from "react-router-dom";
import logo from "@/assets/huelo-logo.svg";

const EFFECTIVE_DATE = "June 10, 2026";

const Privacy = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Privacy Policy — Huelo";
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content") ?? "";
    meta?.setAttribute(
      "content",
      "How Huelo collects, uses, and protects your data when you use our AI room redesign service.",
    );
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <div className="min-h-[100dvh] bg-background">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" aria-label="Huelo home" className="flex items-center gap-2">
            <img src={logo} alt="Huelo" className="h-7 w-auto" />
          </Link>
          <Link
            to="/"
            className="text-sm text-foreground underline underline-offset-4 hover:opacity-80"
          >
            Back to app
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 prose prose-sm sm:prose-base prose-headings:font-display prose-headings:text-foreground prose-headings:tracking-tight prose-h2:mt-10 prose-h2:mb-3 prose-h2:text-xl prose-p:text-foreground/80 prose-p:leading-relaxed prose-li:text-foreground/80 prose-li:my-1 prose-ul:my-3 prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground prose-strong:font-semibold prose-hr:border-border">
        <h1 className="font-display text-2xl md:text-4xl tracking-tight text-foreground mb-2">
          Privacy Policy
        </h1>
        <p className="text-sm text-muted-foreground mb-8">
          Effective date: {EFFECTIVE_DATE}
        </p>

        <p>
          This Privacy Policy describes how <strong>Huelo, Inc.</strong> ("Huelo,"
          "we," "us," or "our") collects, uses, shares, and protects information
          when you use the Huelo website, mobile-friendly web app, and related
          services (collectively, the "Service"). Huelo is a Delaware, USA
          corporation. By using the Service, you agree to this Policy.
        </p>

        <h2>1. Who we are &amp; how to reach us</h2>
        <p>
          <strong>Controller:</strong> Huelo, Inc., Delaware, USA. Privacy
          questions, requests, or complaints:{" "}
          <a href="mailto:hello@huelo.co">hello@huelo.co</a>.
        </p>

        <h2>2. Information we collect</h2>
        <p>We collect the following categories of information:</p>
        <p>
          <strong>Account &amp; identity data</strong> — email address, name
          (optional), authentication identifiers (including Google OAuth
          subject IDs if you sign in with Google), invite code used, and
          account creation timestamp.
        </p>
        <p>
          <strong>Waitlist data</strong> — email and optional name submitted on
          our waitlist form.
        </p>
        <p>
          <strong>Room photos &amp; design inputs</strong> — images you upload
          of your space, plus questionnaire answers describing your style,
          budget, room type, and preferences.
        </p>
        <p>
          <strong>AI-generated content</strong> — redesigned room images, design
          variations, written design rationales, and furniture suggestions
          produced for you, which we store so you can revisit them.
        </p>
        <p>
          <strong>Saved items &amp; activity</strong> — designs and furniture
          you save, ratings or feedback you submit (e.g. NPS), and items
          wishlisted. Some saved items (like wishlists) may be stored in your
          local browser storage rather than on our servers.
        </p>
        <p>
          <strong>Usage &amp; device data</strong> — pages viewed, clicks,
          scrolls, mouse movements, session recordings, referrer, IP address,
          browser, operating system, device type, language, and approximate
          location derived from IP. Some sessions may be recorded via{" "}
          <strong>session replay</strong> to help us understand how the product
          is used and debug issues. These recordings capture your interactions
          with the interface and may include the content displayed on screen,
          though we configure our tools to mask sensitive fields where possible.
        </p>
        <p>
          <strong>Email engagement</strong> — whether transactional or marketing
          emails are delivered, opened, clicked, bounced, or marked as spam,
          and unsubscribe status.
        </p>
        <p>
          <strong>Billing data (if/when paid plans launch)</strong> — billing is
          handled by our payment processor (such as Stripe or a similar
          PCI-compliant provider). We receive limited metadata such as plan
          tier, billing country, payment status, and last four digits of the
          card, but not full card numbers.
        </p>
        <p>
          <strong>Cookies &amp; similar technologies</strong> — used for
          authentication sessions, analytics, advertising attribution, and
          product preferences. See "Cookies &amp; tracking" below.
        </p>

        <h2>3. How we use your information</h2>
        <p>We use your information to:</p>
        <ul>
          <li>Provide, operate, and secure the Service and your account.</li>
          <li>
            Generate AI room redesigns, variations, and furniture suggestions
            based on your photos and questionnaire answers.
          </li>
          <li>
            Store your designs and generated content so you can revisit and
            manage them from your account.
          </li>
          <li>
            Send transactional emails (welcome, waitlist invites, password
            resets, account notices) and, if you opt in, product updates and
            marketing.
          </li>
          <li>
            Measure product performance, debug issues, and improve the Service.
          </li>
          <li>
            <strong>Improve our AI and Service quality</strong> — we may use
            uploaded room photos, generated images, questionnaire answers, and
            interaction data (e.g. which designs you save, rate, or discard) to
            evaluate output quality, fix bugs, fine-tune prompts, and improve
            our models and recommendations. Where possible, we remove or
            pseudonymize direct identifiers (such as your email address) before
            using data for improvement and aggregate data across many users.
            You can opt out of this improvement use (see "Your choices &amp;
            rights").
          </li>
          <li>
            Run advertising and attribution (e.g. measuring which signups came
            from a Google Ads campaign). We currently use these tools to
            measure conversions and optimize campaigns, not to build
            cross-site behavioral profiles of individual users.
          </li>
          <li>
            Prevent fraud, abuse, and violations of our Terms; comply with
            legal obligations.
          </li>
        </ul>

        <h2>4. Legal bases (EEA / UK users)</h2>
        <p>Where GDPR applies, we rely on the following legal bases:</p>
        <ul>
          <li>
            <strong>Performance of a contract</strong> — to deliver the Service
            you request.
          </li>
          <li>
            <strong>Legitimate interests</strong> — to secure, debug, analyze,
            and improve the Service and our AI models, and to market to
            existing users, in ways that do not override your rights and
            freedoms.
          </li>
          <li>
            <strong>Consent</strong> — for marketing emails and non-essential
            cookies, where required.
          </li>
          <li>
            <strong>Legal obligations</strong> — to comply with applicable laws
            and regulations.
          </li>
        </ul>

        <h2>5. How we share information</h2>
        <p>
          We do not sell your personal information. We share it only with:
        </p>
        <ul>
          <li>
            <strong>Service providers / sub-processors</strong> acting on our
            instructions, including:
            <ul>
              <li>
                <strong>Supabase</strong> — managed database, authentication,
                file storage, and edge function hosting (data hosted in the US).
              </li>
              <li>
                <strong>Lovable AI Gateway and underlying model providers</strong>{" "}
                — to run image generation and language models on your room
                photos and questionnaire inputs.
              </li>
              <li>
                <strong>SerpAPI</strong> — to query Google Shopping for
                furniture suggestions matching your design (queries derived
                from your inputs are sent; your account identity is not).
              </li>
              <li>
                <strong>Resend</strong> — to deliver transactional and waitlist
                emails.
              </li>
              <li>
                <strong>PostHog</strong> — product analytics and session
                replay. PostHog collects usage data such as page views, clicks,
                IP address, device and browser information, and may record
                anonymized session replays to help us debug and improve the
                Service.
              </li>
              <li>
                <strong>Google Ads / Google Tag</strong> — conversion
                measurement and advertising attribution.
              </li>
            </ul>
          </li>
          <li>
            <strong>Authorities</strong> when required by law, subpoena, court
            order, or to protect rights, safety, or property.
          </li>
          <li>
            <strong>Successors</strong> in connection with a merger,
            acquisition, financing, or sale of assets, subject to this Policy.
          </li>
        </ul>
        <p>
          Some recipients are located in the United States and other countries.
          For transfers of personal data from the EEA/UK to countries that may
          not have the same level of data protection, we rely on Standard
          Contractual Clauses approved by the European Commission (and the UK
          Addendum where applicable) or other appropriate safeguards, where
          required by law.
        </p>

        <h2>6. AI processing &amp; your content</h2>
        <p>
          When you upload a room photo and complete the questionnaire, the
          image and your inputs are sent to our AI providers (via the Lovable
          AI Gateway) to generate redesigns, variations, and furniture
          suggestions. Generated images are stored in our database and
          associated with your account so you can revisit them from your
          dashboard.
        </p>
        <p>
          We instruct our providers not to use your content to train their
          public foundation models. Providers may process and briefly retain
          content as needed to deliver the service, monitor for abuse, and
          enforce their safety and usage policies.
        </p>
        <p>
          We may use your content (including room photos, generated images,
          questionnaire answers, and related interaction signals) to evaluate
          and improve our Service and our AI prompting/recommendation logic.
          Where feasible, we remove or pseudonymize direct identifiers and
          aggregate data across users for this purpose. If you opt out, we
          will stop using your content in our internal evaluation and
          improvement workflows on a going-forward basis. We may still process
          your content as needed to provide the Service you request, comply
          with law, or enforce our terms.
        </p>

        <h2>7. Data retention</h2>
        <p>
          <strong>Account &amp; design data</strong> — we generally retain your
          profile, uploaded photos, and generated room images, design
          variations, written design rationales, and furniture suggestions for
          as long as your account is active so you can revisit your designs.
          If you delete your account, we delete your profile, saved designs,
          and uploaded photos from our active systems after a short processing
          period. Some information may continue to exist in temporary backups
          for a limited period before being overwritten in the normal course
          of business.
        </p>
        <p>
          <strong>Waitlist data</strong> — retained until you ask us to delete
          it or unsubscribe.
        </p>
        <p>
          <strong>Email logs &amp; suppression lists</strong> — retained as
          needed to honor unsubscribe and spam-compliance obligations.
        </p>
        <p>
          <strong>Analytics &amp; logs</strong> — retained for as long as
          reasonably necessary for product analytics, security, and debugging,
          then deleted or anonymized.
        </p>
        <p>
          We may retain certain information if necessary to comply with legal
          obligations, resolve disputes, or enforce our agreements.
        </p>

        <h2>8. Your choices &amp; rights</h2>
        <p>You can:</p>
        <ul>
          <li>Update your profile information in the app.</li>
          <li>
            Delete your account (Account → Delete account). Deleting your
            account removes your profile, designs, and uploaded photos from
            our active systems after a short processing period, subject to
            backup retention as described above.
          </li>
          <li>
            Unsubscribe from marketing emails via the link in any email or by
            emailing us. We may still send you transactional messages related
            to your account or the Service.
          </li>
          <li>
            Clear locally stored saved items (like wishlists) by clearing your
            browser storage.
          </li>
        </ul>
        <p>
          To <strong>opt out of the use of your content to improve our
          AI/Service</strong>, email{" "}
          <a href="mailto:hello@huelo.co">hello@huelo.co</a> with the
          subject line "Opt out of AI improvement." This opt-out does not
          affect processing needed to provide the Service, prevent fraud, or
          comply with legal obligations.
        </p>
        <p>
          <strong>EEA / UK residents</strong> have the right to access,
          rectify, erase, restrict, or object to processing of their personal
          data, the right to data portability, and the right to lodge a
          complaint with their local supervisory authority.
        </p>
        <p>
          <strong>California residents (CCPA/CPRA)</strong> have the right to
          know what personal information we collect, to request deletion or
          correction, to opt out of "sharing" for cross-context behavioral
          advertising (where applicable), and to be free from discrimination
          for exercising these rights. We do not sell personal information. To
          exercise any right, contact{" "}
          <a href="mailto:hello@huelo.co">hello@huelo.co</a>. We will
          verify your request using your account email and may ask for
          additional information to confirm your identity.
        </p>

        <h2>9. Cookies &amp; tracking</h2>
        <p>
          We use first-party cookies and local storage for authentication
          (Supabase session), product preferences, and saved items. We use
          third-party tags from PostHog (analytics &amp; session replay) and
          Google (Ads conversion measurement and attribution). These tools use
          cookies or similar identifiers to recognize returning browsers and
          help us understand how users interact with the Service.
        </p>
        <p>
          You can block or delete cookies in your browser settings. Some
          Service features (like staying signed in and accessing your
          dashboard) will not work without essential authentication cookies.
        </p>

        <h2>10. Security</h2>
        <p>
          We use reasonable administrative, technical, and physical safeguards
          to protect your information, including:
        </p>
        <ul>
          <li>
            Encryption in transit (HTTPS) and encryption at rest for our
            database and storage.
          </li>
          <li>
            Scoped Row Level Security policies so users can only access their
            own data.
          </li>
          <li>
            Hashed credentials and least-privilege access for our team.
          </li>
          <li>
            Access-controlled storage for user uploaded images so they are
            only accessible to you and authorized Huelo systems.
          </li>
        </ul>
        <p>
          No method of transmission or storage is 100% secure, and we cannot
          guarantee absolute security. If we become aware of a breach
          affecting your personal information, we will notify you and relevant
          authorities when required by law.
        </p>

        <h2>11. Children</h2>
        <p>
          Huelo is not directed to children. We do not knowingly collect
          personal information from anyone under 13 (or under 16 in the EEA
          and UK). If you believe a child has provided us with personal
          information, please contact us and we will delete it.
        </p>

        <h2>12. International users</h2>
        <p>
          We operate from the United States. By using the Service from outside
          the US, you understand your information will be processed in the US
          and other jurisdictions where our providers operate, which may have
          different data protection laws than your home country. Where
          required, we use appropriate safeguards such as Standard Contractual
          Clauses to protect your personal data when it is transferred
          internationally.
        </p>

        <h2>13. Changes to this Policy</h2>
        <p>
          We may update this Policy from time to time. Material changes will
          be announced in-app or by email at least 7 days before they take
          effect. Continued use of the Service after the effective date
          constitutes acceptance of the updated Policy.
        </p>

        <h2>14. Contact</h2>
        <p>
          <strong>Huelo, Inc.</strong>
          <br />
          Email: <a href="mailto:hello@huelo.co">hello@huelo.co</a>
        </p>

        <hr className="my-10" />
        <p className="text-xs text-muted-foreground">
          This Policy is provided for transparency and does not constitute
          legal advice. Please consult counsel before relying on it for
          compliance purposes.
        </p>
      </main>
    </div>
  );
};

export default Privacy;
